import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../services/dashboard.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { buildDashboardThemeVars } from '../dashboard-widget-theme';

interface KpiValue {
	current: number;
	previous: number;
	delta: number;
	percent: number | null;
	format: 'currency' | 'number';
}

interface KpiSecondary {
	label: string;
	value: KpiValue;
}

interface KpiTileData {
	primary: KpiValue;
	secondary?: KpiSecondary;
	denominator?: number;
}

interface SalesmanKpis {
	tiles: {
		invoiced: KpiTileData;
		orders: KpiTileData;
		customers: KpiTileData;
	};
	period: {
		current_start: string;
		current_end: string;
		previous_start: string;
		previous_end: string;
		mode?: 'mtd_default' | 'months';
	};
	salesman: string | null;
}

interface KpiTileDef {
	key: 'invoiced' | 'orders' | 'customers';
	label: string;
	icon: string;
	enabled: boolean;
}

@Component({
	selector: 'app-salesman-kpis',
	templateUrl: './salesman-kpis.component.html',
	styleUrls: ['./salesman-kpis.component.scss']
})
export class SalesmanKpisComponent implements OnInit, OnDestroy {
	kpis: SalesmanKpis | null = null;
	loading = true;

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('skp', 'success');
	}
	config: any = false;
	tiles: KpiTileDef[] = [];

	/** YYYY-MM for optional comparison (sent only when Apply is used). */
	kpiPrimaryYm = '';
	kpiCompareYm = '';

	private subs: Subscription[] = [];

	constructor(
		private dashboardService: DashboardService,
		private globalSearchService: GlobalSearchService,
	) {}

	ngOnInit(): void {
		const y = this.suggestedYmPair();
		this.kpiPrimaryYm = y.primary;
		this.kpiCompareYm = y.compare;
		this.subs.push(this.globalSearchService.configsubscription.subscribe((r: any) => {
			this.config = r;
			this.tiles = this.buildTiles();
		}));
		this.reload();
	}

	ngOnDestroy(): void {
		this.subs.forEach(s => s?.unsubscribe());
		this.subs = [];
	}

	reload(): void {
		this.fetchKpis(null);
	}

	applyCustomMonths(): void {
		if (!this.kpiPrimaryYm || !this.kpiCompareYm) {
			return;
		}
		this.fetchKpis({ kpis_primary_ym: this.kpiPrimaryYm, kpis_compare_ym: this.kpiCompareYm });
	}

	resetToRollingMtd(): void {
		const y = this.suggestedYmPair();
		this.kpiPrimaryYm = y.primary;
		this.kpiCompareYm = y.compare;
		this.fetchKpis(null);
	}

	private fetchKpis(period: { kpis_primary_ym: string; kpis_compare_ym: string } | null): void {
		this.loading = true;
		this.subs.push(this.dashboardService.getSalesmanKpis(period).subscribe(
			(result: any) => {
				this.kpis = result && result.tiles ? result as SalesmanKpis : null;
				this.loading = false;
			},
			() => {
				this.loading = false;
			},
		));
	}

	private suggestedYmPair(): { primary: string; compare: string } {
		const now = new Date();
		const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
		return {
			primary: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
			compare: `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`,
		};
	}

	getTile(tile: KpiTileDef): KpiTileData | null {
		if (!this.kpis || !this.kpis.tiles) {
			return null;
		}
		return this.kpis.tiles[tile.key] || null;
	}

	trendIcon(value: KpiValue | null): string {
		const dir = this.direction(value);
		if (dir === 'up') return 'fa-long-arrow-up';
		if (dir === 'down') return 'fa-long-arrow-down';
		return 'fa-minus';
	}

	chipClass(value: KpiValue | null): string {
		const dir = this.direction(value);
		if (dir === 'up') return 'is-up';
		if (dir === 'down') return 'is-down';
		return 'is-flat';
	}

	trendLabel(value: KpiValue | null): string {
		if (!value) return '—';
		if (value.previous === 0 && value.current === 0) return '0%';
		if (value.percent === null || value.percent === undefined) return '—';
		const pct = Math.abs(value.percent) * 100;
		const sign = value.percent > 0 ? '+' : value.percent < 0 ? '-' : '';
		return `${sign}${pct.toFixed(1)}%`;
	}

	private direction(value: KpiValue | null): 'up' | 'down' | 'flat' {
		if (!value) return 'flat';
		if (value.delta > 0) return 'up';
		if (value.delta < 0) return 'down';
		return 'flat';
	}

	private buildTiles(): KpiTileDef[] {
		const cfg = this.config?.dashboardSalesmanKpiTiles || {};

		const defaults: KpiTileDef[] = [
			{ key: 'invoiced', label: 'Invoiced MTD', icon: 'attach_money', enabled: true },
			{ key: 'orders', label: 'Orders MTD', icon: 'assignment', enabled: true },
			{ key: 'customers', label: 'Active Customers MTD', icon: 'people', enabled: true },
		];

		return defaults.map((tile) => {
			if (Object.prototype.hasOwnProperty.call(cfg, tile.key)) {
				return { ...tile, enabled: !!cfg[tile.key] };
			}
			return tile;
		}).filter((tile) => tile.enabled);
	}
}
